import {
  PRIVATE_SONG_SENDER,
  ACCESS_TYPE_OPTIONS,
  EVENT_KIND_OPTIONS,
  MUSIC_TYPE_OPTIONS,
  VISIBILITY_OPTIONS,
  EXT_SOURCE_OPTIONS,
} from "@libs/utils";
import { accessGemEconomicsQuoteWireSchema } from "@libs/gamification-rewards-contracts";
import { eventStartLocalSchema } from "./event-time";
import { merchOrderViewSchema, purchaseSchema } from "@libs/platform-wire";
import { z } from "zod";
import { accessLineupSchema } from "./access-lineup";
import { normalizeIsrc, isValidIsrc, isValidTemporaryIsrc } from "@libs/catalog-engine-contracts/song";

const accessCollaboratorSchema = z.object({
  id: z.string().describe("The artist's id."),
  name: z.string().trim().describe("The artist's name."),
  image: z.string().nullable().describe("The artist's picture URL; null for none."),
});

export const baseAccessSchema = z.object({
  createdAt: z.string(),
  updatedAt: z.string(),
  id: z.string(),
  artistId: z.string(),
  extLinks: z.array(z.string()).nullable(),
  type: z.enum(ACCESS_TYPE_OPTIONS),
  boost: z.number(),
  isPinned: z.boolean().nullable(),
  title: z.string(),
  artistName: z.string().trim(),
  artistImage: z.string().nullable(),
  collaborators: z.array(accessCollaboratorSchema),
  lineup: accessLineupSchema.optional(),
  artists: z.array(accessCollaboratorSchema).optional(),
  engineEventId: z.string().nullable().optional(),
  gemEconomicsQuote: accessGemEconomicsQuoteWireSchema.nullable().optional(),
});

export type BaseAccessType = z.infer<typeof baseAccessSchema>;

export const accessTypeMusicSchema = baseAccessSchema.extend({
  type: z.literal("music"),
  musicAccessType: z.enum(MUSIC_TYPE_OPTIONS),
  unlocked: z.boolean(),
  price: z.number().min(0),
  priceInLamports: z.number().min(0).optional(),
  priceMin: z.number().min(0).nullable(),
  priceMax: z.number().min(0).nullable(),
  priceCurrency: z.string(),
  extSource: z.enum(EXT_SOURCE_OPTIONS).nullable(),
  /** Members-only music (LIS-1156): its owner artist's fan club's current
   *  members own it, and nobody buys or unlocks it. Defaulted, so an access
   *  cached before the field was read still parses. */
  membersOnly: z.boolean().default(false),
  /** Whose fan club members-only music is for: its owner artist's name,
   *  as the Live Link's wire says it; null for any other music. */
  clubArtistName: z.string().nullable().default(null),
  /** Where a fan joins members-only music's club: its owner artist's Live
   *  Link, while their club is open and the page published; null otherwise. */
  clubUrl: z.string().nullable().default(null),
  songs: z
    .array(
      z.object({
        id: z.string(),
        albums: z.array(
          z.object({
            id: z.string(),
            title: z.string(),
            image: z.string(),
            trackNumber: z.number(),
          }),
        ),
        title: z.string(),
        previewUrl: z.string().nullable(),
        duration: z.number(),
        isSetList: z.boolean().default(false),
      }),
    )
    .min(1),
});

export type AccessTypeMusicType = z.infer<typeof accessTypeMusicSchema>;

// A vendor order of the owner's tickets: platform-wire's, so Link in Bio's
// My Tickets and the app parse one contract. A merch order likewise.
export {
  vendorOrderViewSchema,
  merchOrderViewSchema,
  merchDeliveryAddressSchema,
  type VendorOrderView,
  type VendorTicketDocument,
  type MerchOrderView,
  type MerchDeliveryAddress,
} from "@libs/platform-wire";

/** Whether an event asks a fan for a detail: not at all, if they like, or
 *  before they can continue. Email is always required and has no rule. */
export const buyerDetailRuleSchema = z.enum(["off", "optional", "required"]);
export type BuyerDetailRule = z.infer<typeof buyerDetailRuleSchema>;

export const accessTypeEventSchema = baseAccessSchema.extend({
  type: z.literal("event"),
  kind: z.enum(EVENT_KIND_OPTIONS).nullable().optional(),
  // The event's start INSTANT — for LOGIC only (sorting, expiry badges, scan gating).
  // NEVER render it: display comes from `startsAtLocal`, resolved server-side into
  // the venue's own wall clock (2026-07-23 operator decision — clients do zero
  // timezone work; a France concert reads as France time on every device).
  startDateTime: z.string(),
  startsAtLocal: eventStartLocalSchema,
  endsAtLocal: eventStartLocalSchema.nullable(),
  endDateTime: z.string().nullable(),
  /** Set once the event is cancelled: show it as cancelled, with no way to buy. */
  cancelledAt: z.string().nullable().optional(),
  city: z.string(),
  venue: z.string(),
  /** Optional full street address; the venue name stays separate. */
  address: z.string().nullable().optional(),
  /** Fan-page section order: Location above the Line-up when true. */
  locationFirst: z.boolean().optional(),
  /** What a fan must give to get a ticket, beyond their email (LIS-1000). */
  buyerNameRule: buyerDetailRuleSchema.optional(),
  buyerPhoneRule: buyerDetailRuleSchema.optional(),
  country: z.string(),
  coverImageUrl: z.string().nullable(),
  description: z.string().nullable(),
  instructions: z.string().nullable(),
  instructionsImage: z.string().nullable(),
  price: z.number().min(0).nullable(),
  /** `price` is the lowest of several ticket prices: render as "From …". */
  priceFrom: z.boolean().optional(),
  priceMin: z.number().min(0).nullable().optional(),
  priceMax: z.number().min(0).nullable().optional(),
  priceCurrency: z.string(),
  bookingFee: z.number().min(0).nullable(),
  priceInLamports: z.number().min(0).optional(),
  imageAttachment: z.string().nullable(),
  waitlistTicketAmount: z.number().nullable().optional(),
  isWaitlist: z.boolean(),
  userTickets: z
    .array(
      z.object({
        id: z.string(),
        originalPrice: z.number(),
        purchasePrice: z.number(),
        redeemCode: z.string().nullable(),
        /** Bought on the fan's behalf from its seller (a vendor checkout's):
         *  its codes are the seller's, and My Tickets draws its `collect`. */
        fromSeller: z.boolean(),
        vendorAttemptId: z.string().nullable(),
        vendorBookingReference: z.string().nullable(),
        vendorTicketName: z.string().nullable(),
        isWaitlist: z.boolean(),
        claimed: z.boolean(),
      }),
    )
    .nullable(),
});

export type AccessTypeEventType = z.infer<typeof accessTypeEventSchema>;

/**
 * A merch product sold through a vendor's store (Music Glue) and bought on
 * the fan's behalf, as Link in Bio's merch page serves it
 * (`linkPageMerchAccessSchema`): its picture, description and the
 * headline price of its cheapest size (`merchHeadline`). Only a caller that
 * asks for merch receives it, so builds that predate it never do.
 */
export const accessTypeMerchSchema = baseAccessSchema.extend({
  type: z.literal("merch"),
  /** What is sold, as its store names it ("T-Shirt (TOUR)"). */
  item: z.string(),
  description: z.string().nullable(),
  /** The product's picture, resolved for display; null when none. */
  imageUrl: z.string().nullable(),
  /** The cheapest size on sale at quantity one, without UK delivery; null
   *  while nothing can be bought (no live listing, or every size gone). */
  price: z
    .object({
      amount: z.number().nonnegative(),
      currency: z.string().min(1),
      /** Sizes at different prices: render as "From …". */
      from: z.boolean().optional(),
    })
    .nullable(),
  /** The UK delivery that size costs, in minor units; null with `price`,
   *  and where the store quotes its own for each basket (a Shopify
   *  store's: "+ UK delivery"). */
  deliveryMinor: z.number().int().nonnegative().nullable(),
  /** Its seller adds tax at checkout (LIS-1133): `price` is before tax, and
   *  reads "+ local tax" (`merchPriceLabel`). Absent otherwise, and for a
   *  read that names no journey version, which is given no such listing.
   *  Optional, so builds and caches from before it keep parsing. */
  taxAdded: z.boolean().optional(),
  /** A digital download: the store emails it, nothing is posted, so no
   *  delivery is quoted or added (`access_type_merch.download`). Absent:
   *  posted. Optional, so builds and caches from before it keep parsing. */
  download: z.literal(true).optional(),
  instructions: z.string().nullable().optional(),
  /** The signed-in viewer's own orders of it, newest first: absent while
   *  they hold none, and for anyone else. Optional, so caches stored
   *  before orders were read keep parsing. */
  orders: z.array(merchOrderViewSchema).optional(),
});

export type AccessTypeMerchType = z.infer<typeof accessTypeMerchSchema>;

export const getAccessOutputSchema = z.discriminatedUnion("type", [
  accessTypeEventSchema,
  accessTypeMusicSchema,
  accessTypeMerchSchema,
]);

// export const getAccessTypeConcertOutputSchema = baseAccessSchema.and(
//   accessTypeConcertSchema,
// );

// export type AccessTypeConcertOutputType = z.infer<
//   typeof getAccessTypeConcertOutputSchema
// >;

export type Access = z.infer<typeof getAccessOutputSchema>;

/**
 * One access as the app opens it (`accesses.getById`), with its purchase
 * (LIS-1173): what the purchase sheet draws, compiled by the server for the
 * fan, with the signed-in buyer's own answers in its fields. Null where the
 * access offers no listing. Only this read carries it: lists carry a
 * headline price.
 */
export const getAccessByIdOutputSchema = z.discriminatedUnion("type", [
  accessTypeEventSchema.extend({ purchase: purchaseSchema.nullable() }),
  accessTypeMusicSchema,
  accessTypeMerchSchema.extend({ purchase: purchaseSchema.nullable() }),
]);
export type AccessById = z.infer<typeof getAccessByIdOutputSchema>;

export const claimItemOutputSchema = z.object({
  valid: z.boolean(),
  user: z
    .object({
      id: z.string(),
      name: z.string(),
      image: z.string()?.optional(),
      blurhash: z.string()?.optional(),
    })
    .nullable(),
  scannedBefore: z.boolean(),
  userEmail: z.string().optional(), // For anonymous tickets
});

export const voucherAccessUpsertSchema = z.object({
  voucherAccessId: z
    .string()
    .length(36)
    .describe(
      "The voucher's id, a UUID: a saved voucher's id keeps it, a new random one adds a voucher.",
    ),
  gemCount: z
    .number()
    .min(1)
    .describe("How many Gems a fan spends to get the voucher."),
  discountValue: z
    .number()
    .min(1)
    .describe("The discount the voucher gives on the access."),
});

const segmentRuleSchema = z.discriminatedUnion("type", [
  z.object({
    type: z
      .literal("checked_out_access")
      .describe('"checked_out_access": the fan has checked out another access.'),
    accessId: z
      .string()
      .length(36)
      .describe("The access the fan must have checked out."),
    requireClaimed: z
      .boolean()
      .optional()
      .describe(
        "True to require that the fan claimed their ticket for that access, not only bought it.",
      ),
  }),
  // Add more rule types here as needed
]);

const segmentConfigSchema = z
  .object({
    logic: z
      .enum(["AND", "OR"])
      .describe("AND: a fan must meet every rule. OR: any one rule is enough."),
    rules: z
      .array(segmentRuleSchema)
      .describe("The rules a fan is checked against; with none, everyone qualifies."),
  })
  .nullable();

export const baseAccessUpsertSchema = z.object({
  type: z.enum(ACCESS_TYPE_OPTIONS).describe("The kind of access."),
  accessId: z
    .string()
    .length(36)
    .describe(
      "The access's id, a UUID: an existing access's id saves over it, a new random one creates it.",
    ),
  artistId: z
    .string()
    .length(36)
    .describe("The headlining artist's id (lineup position 1)."),
  extLinks: z
    .array(z.string().min(1, "Can't be empty").describe("A URL."))
    .min(1)
    .nullable()
    .describe(
      "External links; the first is the URL fans are redirected to. Null for an internal access.",
    ),
  boost: z
    .number()
    .min(0)
    .describe("A whole number, 0 or more, that raises the access in recommendations; 0 for none."),
  isPinned: z
    .boolean()
    .describe("True pins the access to the top of the artist's accesses."),
  title: z
    .string()
    .min(1, "Can't be empty")
    .describe("The name fans see, such as the event's name."),
  visibility: z
    .enum(VISIBILITY_OPTIONS)
    .describe("Who sees the access: public, or fans_only."),
  vouchers: z
    .array(voucherAccessUpsertSchema)
    .describe(
      "The access's public Gem vouchers, replacing the saved ones: a saved voucher left out is deleted. Send an empty list for none.",
    ),
  voucherSegmentConfig: segmentConfigSchema.describe(
    "Which fans qualify for the vouchers, by rules; null for no rule, so everyone qualifies.",
  ),
  collaborators: z
    .array(accessCollaboratorSchema)
    .optional()
    .describe(
      "Collaborating artists, replacing the saved ones. Ignored when lineup is sent.",
    ),
  lineup: accessLineupSchema
    .optional()
    .describe(
      "The acts in billing order, replacing the saved lineup: position 1 is the headliner, who must be artistId. Omitted keeps the saved lineup and uses collaborators.",
    ),
});

export type BaseAccessUpsertType = z.infer<typeof baseAccessUpsertSchema>;

export const musicAccessUpsertSchema = baseAccessUpsertSchema.extend({
  type: z.literal("music").describe('Always "music".'),
  musicAccessId: z
    .string()
    .length(36)
    .describe("The music part's id, a UUID: a saved one's id keeps it."),
  songIds: z
    .array(z.string().length(36).describe("A catalog song's id."))
    .min(1)
    .describe(
      "The song the access gives; the first id is used and made private.",
    ),
  musicAccessType: z
    .enum(MUSIC_TYPE_OPTIONS)
    .describe("unreleased, or pre-release for a track ahead of its release."),
  previewUrls: z
    .array(z.string().min(1).describe("A preview URL."))
    .min(1)
    .nullable()
    .describe("Preview URLs fans can play before unlocking; null for none."),
  price: z.number().min(0).describe("The price fans pay to unlock it."),
  startDateTime: z
    .string()
    .datetime()
    .nullable()
    .describe("When it becomes available, an ISO 8601 date-time; null for now."),
  endDateTime: z
    .string()
    .datetime()
    .nullable()
    .describe("When it stops being available, an ISO 8601 date-time; null for never."),
  extSource: z
    .enum(["bandcamp", "bandcamp-ingested", "ern"])
    .optional()
    .describe("The external source of the music, when it has one."),
  // The artist's managers can change the terms in LFP. Left out, a save
  // keeps what the access has.
  managerManaged: z
    .boolean()
    .optional()
    .describe(
      "True lets the artist's managers change its terms in Lissen for Partners. Omitted keeps the saved setting.",
    ),
});

export const eventAccessUpsertSchema = baseAccessUpsertSchema.extend({
  doorsDateTime: z
    .string()
    .datetime()
    .nullable()
    .optional()
    .describe(
      "When doors open, an ISO 8601 date-time at or before the start; null or omitted for none.",
    ),
  type: z.literal("event").describe('Always "event".'),
  startDateTime: z
    .string()
    .datetime()
    .describe("When the show starts, an ISO 8601 date-time."),
  // IANA zone the admin's typed wall clock is in; the server defaults new events to
  // "Europe/London" when absent (null and undefined alike). Nullable so a loaded
  // read access (whose `timezone` is nullable) round-trips through the edit form.
  timezone: z
    .string()
    .nullable()
    .optional()
    .describe(
      "The venue's IANA timezone, such as Europe/London. Omitted or null keeps the saved one, or Europe/London for a new event.",
    ),
  endDateTime: z
    .string()
    .datetime()
    .nullable()
    .describe(
      "When the show ends, an ISO 8601 date-time after the start; null if unknown.",
    ),
  city: z.string().min(1, "Can't be empty").describe("The venue's city."),
  venue: z.string().min(1, "Can't be empty").describe("The venue's name."),
  // Optional full street address, kept apart from the venue name. Omitted or
  // null means "no address"; readers hide the line rather than show blanks.
  address: z
    .string()
    .trim()
    .min(1)
    .nullable()
    .optional()
    .describe(
      "The venue's street address, apart from its name; null or omitted for none.",
    ),
  // Show Location above the Line-up on the fan page. Absent keeps the default
  // order (Line-up first).
  locationFirst: z
    .boolean()
    .optional()
    .describe(
      "True shows the location above the lineup on the fan page; omitted or false keeps the lineup first.",
    ),
  // What a fan must give to get a ticket, beyond their email. Absent leaves
  // the stored rule alone, so an editor that does not know the field (the
  // staff forms) cannot reset it.
  buyerNameRule: buyerDetailRuleSchema
    .optional()
    .describe(
      "Whether a fan gives their name to get a ticket: off, optional or required. Omitted keeps the saved rule.",
    ),
  buyerPhoneRule: buyerDetailRuleSchema
    .optional()
    .describe(
      "Whether a fan gives their phone number to get a ticket: off, optional or required. Omitted keeps the saved rule.",
    ),
  country: z
    .string()
    .min(1, "Can't be empty")
    .describe("The venue's country, as an ISO 3166-1 alpha-2 code such as GB."),
  coverImageUrl: z
    .string()
    .min(1)
    .nullable()
    .describe(
      "The event's poster image URL; null for none, and the fan page then shows the workspace's picture.",
    ),
  description: z
    .string()
    .min(1)
    .nullable()
    .describe("The event's description on its fan page; null for none."),
  instructions: z
    .string()
    .min(1)
    .nullable()
    .describe(
      "Entry instructions for ticket holders, such as what to bring; null for none.",
    ),
  instructionsImage: z
    .string()
    .min(1)
    .nullable()
    .describe(
      "An image URL shown with the entry instructions, such as a map; null for none.",
    ),
  price: z.number().min(0).describe("The ticket price."),
  priceMin: z
    .number()
    .min(0)
    .nullable()
    .describe("The lowest price, equal to price for a fixed price; null for none."),
  priceMax: z
    .number()
    .min(0)
    .nullable()
    .describe("The highest price; null means fans pay what they want."),
  priceCurrency: z
    .string()
    .describe("The price's currency as a code, such as GBP."),
  bookingFee: z
    .number()
    .min(0)
    .nullable()
    .describe("A booking fee added to the price; null for none."),
  waitlistTicketAmount: z
    .number()
    .int()
    .min(0)
    .nullable()
    .optional()
    .describe(
      "How many tickets are sold before the waitlist starts; null or omitted for none.",
    ),
});

export type MusicAccessUpsertType = z.infer<typeof musicAccessUpsertSchema>;

// READ variant (2026-07-22 partner-profile rework): converger-created event accesses
// (rinse radio episodes) legitimately carry no venue/city/country — the UPSERT schema
// rightly requires them of human-authored events, but read paths must never reject
// rows the system itself wrote. Use for `.output(...)`; keep the upsert schema for
// `.input(...)` and form validation.
export const eventAccessReadSchema = eventAccessUpsertSchema.extend({
  unresolvedLineupCount: z.number().int().nonnegative().optional(),
  engineEventId: z.string().nullable().optional(),
  /** Set once the event is cancelled: it is kept, and no longer changed. */
  cancelledAt: z.string().nullable().optional(),
  kind: z.enum(EVENT_KIND_OPTIONS).nullable().optional(),
  city: z.string(),
  venue: z.string(),
  country: z.string(),
  // Read-only date-precision/zone fields (admin is the EDITING surface, so unlike the
  // consumer wire it sees the raw model). The upsert schema deliberately lacks
  // startTimeKnown: admin saves never overwrite what the pipeline derived.
  startTimeKnown: z.boolean().optional(),
  timezone: z.string().nullable().optional(),
  // …and the resolved venue-local components for dumb display in tables/previews.
  startsAtLocal: eventStartLocalSchema.optional(),
});
export type EventAccessReadType = z.infer<typeof eventAccessReadSchema>;

export const accessUpsertSchema = z.discriminatedUnion("type", [
  musicAccessUpsertSchema,
  eventAccessUpsertSchema,
]);

export const accessReadSchema = z.discriminatedUnion("type", [
  musicAccessUpsertSchema.extend({
    unresolvedLineupCount: z.number().int().nonnegative().optional(),
  }),
  eventAccessReadSchema,
]);
export type AccessReadType = z.infer<typeof accessReadSchema>;

export type EventAccessUpsertType = z.infer<typeof eventAccessUpsertSchema>;

export type AccessUpsertType = z.infer<typeof accessUpsertSchema>;

export const privateSongIsrcSchema = z
  .string()
  .trim()
  .toUpperCase()
  .transform((value) =>
    isValidTemporaryIsrc(value) ? value : normalizeIsrc(value),
  )
  .refine(
    (value) => isValidIsrc(value) || isValidTemporaryIsrc(value),
    "Enter a valid ISRC or keep the generated identifier.",
  )
  .describe(
    "The song's ISRC (letters and digits, dashes and spaces allowed), or the temporary identifier the Admin form generates.",
  );

const privateSongDateSchema = z
  .union([
    z.literal("").describe("An empty string: no date."),
    z.iso.date().describe("A date as YYYY-MM-DD."),
  ])
  .describe("A date as YYYY-MM-DD, or an empty string for no date.");

export const addSongSchema = z.object({
  albumArtworkFilePath: z
    .string()
    .min(1)
    .describe(
      "The album artwork's path in the upload bucket: `albumArtworkFilePath` from admin_artists_songs_getSignedUploadUrl, once the file is uploaded.",
    ),
  audioFilePath: z
    .string()
    .min(1)
    .describe(
      "The audio file's path in the upload bucket: `audioFilePath` from admin_artists_songs_getSignedUploadUrl, once the file is uploaded.",
    ),
  audioHash: z
    .string()
    .min(1)
    .describe("The hash (SHA-256, hex) of the uploaded audio file."),
  durationSeconds: z
    .number()
    .positive()
    .describe("The song's length in seconds; leave out when unknown.")
    .optional(),
  songTitle: z.string().min(1).describe("The song's title."),
  isrc: privateSongIsrcSchema,
  pline: z
    .string()
    .min(1)
    .describe("The phonogram copyright (℗) line: the year and owner, for example `2026 Label`."),
  genreIds: z
    .array(
      z
        .string()
        .min(1)
        .describe("A genre id, from admin_catalog_genres_list."),
    )
    .min(1)
    .describe("The song's genres, at least one."),
  albumTitle: z.string().min(1).describe("The album's title."),
  prereleaseDate: privateSongDateSchema.describe(
    "The date the song becomes available ahead of release (YYYY-MM-DD), or an empty string for none.",
  ),
  releaseDate: privateSongDateSchema.describe(
    "The song's release date (YYYY-MM-DD), or an empty string when unknown.",
  ),
  artistId: z
    .string()
    .min(1)
    .describe("The catalog artist's id, from admin_artists_search."),
  sender: z
    .object({
      name: z
        .literal(PRIVATE_SONG_SENDER)
        .describe(`Always \`${PRIVATE_SONG_SENDER}\`, the fixed sender of private uploads.`),
      partyId: z
        .literal(PRIVATE_SONG_SENDER)
        .describe(`Always \`${PRIVATE_SONG_SENDER}\`, the fixed sender of private uploads.`),
    })
    .describe("The delivering party: always the fixed private-upload sender."),
  sobo: z.null().describe("Always null: private uploads are sent on nobody's behalf."),
  label: z
    .object({
      id: z.string().min(1).describe("The label's id, from admin_artists_rights `labels`."),
      name: z.string().min(1).describe("The label's name."),
    })
    .describe("The song's label, one of admin_artists_rights `labels`."),
});

// Private-song edits keep ISRC and artist identity fixed. Omitted assets are retained.
export const editPrivateSongSchema = z.object({
  artistId: z
    .string()
    .uuid()
    .describe("The catalog artist's id, from admin_artists_search."),
  songId: z
    .string()
    .uuid()
    .describe("The private song's id."),
  songTitle: z.string().trim().min(1).describe("The song's title."),
  albumTitle: z.string().trim().min(1).describe("The album's title."),
  pline: z
    .string()
    .trim()
    .min(1)
    .describe("The phonogram copyright (℗) line: the year and owner, for example `2026 Label`."),
  language: z
    .string()
    .trim()
    .describe("The language of performance, or an empty string for none."),
  advisory: z
    .enum(["Explicit", "NotExplicit", "Clean"])
    .describe("The parental advisory, or null for none.")
    .nullable(),
  genreIds: z
    .array(
      z.string().uuid().describe("A genre id, from admin_catalog_genres_list."),
    )
    .min(1)
    .describe("The song's genres, at least one."),
  // Optional for older clients: omission preserves the source delivery's values.
  prereleaseDate: privateSongDateSchema
    .describe(
      "The date the song becomes available ahead of release (YYYY-MM-DD), or an empty string for none; left out keeps the current one.",
    )
    .optional(),
  releaseDate: privateSongDateSchema
    .describe(
      "The song's release date (YYYY-MM-DD), or an empty string for none; left out keeps the current one.",
    )
    .optional(),
  label: addSongSchema.shape.label
    .describe("The song's label; left out keeps the current one.")
    .optional(),
  audio: z
    .object({
      path: z
        .string()
        .regex(/^private-song-originals\/edits\/[a-f0-9-]{36}\/[^/]+$/)
        .describe(
          "The new audio's path in the upload bucket: `audioFilePath` from admin_artists_songs_getSignedUploadUrl called with `forEdit`.",
        ),
      hash: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .describe("The hash (SHA-256, lowercase hex) of the uploaded audio."),
    })
    .describe("Replacement audio, already uploaded; left out keeps the current audio.")
    .optional(),
  artwork: z
    .object({
      path: z
        .string()
        .regex(/^private-song-originals\/[a-f0-9-]{36}$/)
        .describe(
          "The new artwork's path in the upload bucket: `albumArtworkFilePath` from admin_artists_songs_getSignedUploadUrl.",
        ),
      hash: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .describe("The hash (SHA-256, lowercase hex) of the uploaded artwork."),
    })
    .describe("Replacement album artwork, already uploaded; left out keeps the current artwork.")
    .optional(),
});
export type EditPrivateSongType = z.infer<typeof editPrivateSongSchema>;

export type AddSongType = z.infer<typeof addSongSchema>;
